import React from 'react';
import { Route, Switch, Redirect, useHistory, useLocation } from 'react-router-dom';
import { shape, string, bool, arrayOf, number } from 'prop-types';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import { hasFormErrors } from 'utils/form_validaters/gift_flow_helper';
import { ProPageStrings, StringConstantsMixin } from 'react_components/mixins/shared/string_constants_mixin';
import { createGiftCard } from 'utils/requests/gifting_requests';
import { pushHistory, generateLocalizedRoute, localizedRouteIncludes } from 'utils/client_side_routing_helpers';
import { WebProPurchaseLocation } from '@alltrails/analytics/amplitudeEnums';
import * as styles from './styles/styles.module.scss';
import CustomizeGiftForm from '../CustomizeGiftForm';
import { PageContext, LOG_IN_USER } from '../../shared/Page/Page';
import { GiftFlowContext, TOGGLE_PLAN_TYPE, UPDATE_SERVER_ERRORS, UPDATE_PURCHASED_GIFT_CARD } from '../GiftFlow/GiftFlow';
import GiftLandingPage from '../Landing/GiftLandingPage';
import LoginForm from '../../forms/LoginForm';
import GiftCardPrint from '../GiftCardPrint';
import GiftConfirmation from '../GiftConfirmation';
import SignUpForm from '../../forms/SignUpForm';
import PaymentForm from '../../forms/pro/PaymentForm';
import MiniFooter from '../MiniFooter';
import GiftReview from '../GiftReview';

const {
  strings: {
    GIFT_FLOW: {
      PAYMENT_FORM: { GIFT_PAYMENT_HEAD_TEXT, SUBMIT_TEXT }
    }
  }
} = StringConstantsMixin;

const GIFT_PAYMENT_TEXT = {
  ...ProPageStrings.paymentText,
  creditCardForm: ProPageStrings.creditCardForm,
  headText: GIFT_PAYMENT_HEAD_TEXT
};

const MINI_FOOTER_PATHNAMES = ['/gift/payment', '/gift/login', '/gift/signup'];
const INVALID_EMAIL = 'invalid_email';
const UNITED_STATES_COUNTRY_ID = 313;

const GiftFlowRouting = ({
  currencyCode,
  facebookAppId,
  planTypes,
  recurlyPublicKey,
  paymentTypes,
  amazonSellerId,
  amazonClientId,
  navigationMessagingChannel,
  enableRecaptcha,
  shopifyProducts
}) => {
  const languageRegionCode = useLanguageRegionCode();
  const {
    globalState: {
      context: { currentUser, countryId }
    },
    dispatchGlobalState
  } = React.useContext(PageContext);

  const {
    giftFlowState: { giftCustomizeParams, activePlanType, purchasedGiftCard },
    dispatchGiftFlow
  } = React.useContext(GiftFlowContext);
  const history = useHistory();
  const location = useLocation();

  const handleAuthSuccess = data => {
    const { user } = data;
    navigationMessagingChannel.publish({ topic: 'navigation.loginUser', data: { currentUser: user, proUser: user.pro } });
    dispatchGlobalState({ type: LOG_IN_USER, payload: user });

    pushHistory('/gift/payment', history, languageRegionCode);
  };

  const navigateToSignUp = e => {
    e.preventDefault();
    pushHistory('/gift/signup', history, languageRegionCode);
  };

  const navigateToLogin = () => {
    pushHistory('/gift/login', history, languageRegionCode);
  };

  const updateActivePlan = payload => {
    dispatchGiftFlow({ type: TOGGLE_PLAN_TYPE, payload });
  };

  const handlePaymentSuccess = payload => {
    Object.assign(payload, { isEmail: !!payload.metadata.deliveryDate });
    dispatchGiftFlow({ type: UPDATE_PURCHASED_GIFT_CARD, payload });

    pushHistory('/gift/review', history, languageRegionCode);
  };

  const handlePaymentError = err => {
    if (!err || !err.responseJSON) {
      return err;
    }

    const { errors } = err.responseJSON;
    const invalidEmailErrors = errors ? errors.filter(error => error.code === INVALID_EMAIL) : [];

    if (invalidEmailErrors.length > 0) {
      dispatchGiftFlow({ type: UPDATE_SERVER_ERRORS, payload: invalidEmailErrors });
      pushHistory('/gift/customize', history, languageRegionCode);
    }

    return err;
  };

  const handlePaymentSubmit = creditCardInfo =>
    createGiftCard(creditCardInfo, giftCustomizeParams, currencyCode, languageRegionCode).then(handlePaymentSuccess).catch(handlePaymentError);

  return (
    <>
      <Switch>
        <Route path="*/gift" exact>
          <GiftLandingPage currencyCode={currencyCode} planTypes={planTypes} />
        </Route>
        <Route path="*/gift/customize" exact>
          <CustomizeGiftForm />
        </Route>
        <Route path="*/gift/login" exact>
          {currentUser ? (
            <Redirect to={generateLocalizedRoute('/gift', languageRegionCode)} />
          ) : (
            <div className={styles.formContainer}>
              <LoginForm
                enableRecaptcha={enableRecaptcha}
                locale={languageRegionCode}
                handleLoginSuccess={handleAuthSuccess}
                handleRegisterClick={navigateToSignUp}
                facebookAppId={facebookAppId}
              />
            </div>
          )}
        </Route>
        <Route path="*/gift/signup" exact>
          {currentUser ? (
            <Redirect to={generateLocalizedRoute('/gift', languageRegionCode)} />
          ) : (
            <div className={styles.formContainer}>
              <SignUpForm
                countryId={countryId}
                handleRegistrationSuccess={handleAuthSuccess}
                handleLoginClick={navigateToLogin}
                enableRecaptcha={enableRecaptcha}
                facebookAppId={facebookAppId}
                redirectTo={null}
              />
            </div>
          )}
        </Route>
        <Route path="*/gift/payment" exact>
          {hasFormErrors(giftCustomizeParams) ? (
            <Redirect to={generateLocalizedRoute('/gift/customize', languageRegionCode)} />
          ) : (
            <div className={styles.formContainer}>
              {giftCustomizeParams.sendMethod === 'email' && (
                <GiftConfirmation
                  recipientName={giftCustomizeParams.recipientName}
                  recipientEmail={giftCustomizeParams.recipientEmail}
                  sendDate={giftCustomizeParams.sendDate}
                  languageRegionCode={languageRegionCode}
                />
              )}
              <PaymentForm
                submitText={SUBMIT_TEXT}
                text={GIFT_PAYMENT_TEXT}
                locale={languageRegionCode}
                recurlyPublicKey={recurlyPublicKey}
                activePlanType={activePlanType}
                planTypes={planTypes}
                isCouponInputExpanded={false}
                isCouponInputShown={false}
                paymentTypes={paymentTypes}
                amazonClientId={amazonClientId}
                amazonSellerId={amazonSellerId}
                currencyCode={currencyCode}
                enableRecaptcha={enableRecaptcha}
                updateActivePlan={updateActivePlan}
                handlePayment={handlePaymentSubmit}
                webProPurchaseLocation={WebProPurchaseLocation.GiftPage}
                hideFinePrint
                showPayWithCard
              />
            </div>
          )}
        </Route>
        <Route path="*/gift/review" exact>
          {!purchasedGiftCard ? (
            <Redirect to={generateLocalizedRoute('/gift', languageRegionCode)} />
          ) : (
            <GiftReview {...purchasedGiftCard} shopifyProducts={shopifyProducts} showShopifyPreview={countryId === UNITED_STATES_COUNTRY_ID} />
          )}
        </Route>
        <Route path="*/gift/preview" exact>
          {!purchasedGiftCard ? (
            <Redirect to={generateLocalizedRoute('/gift', languageRegionCode)} />
          ) : (
            <GiftCardPrint {...purchasedGiftCard} languageRegionCode={languageRegionCode} />
          )}
        </Route>
        <Route path="*/gift*" exact>
          <Redirect to={generateLocalizedRoute('/gift', languageRegionCode)} />
        </Route>
      </Switch>
      {localizedRouteIncludes(location.pathname, MINI_FOOTER_PATHNAMES, languageRegionCode) && <MiniFooter />}
    </>
  );
};
GiftFlowRouting.propTypes = {
  currencyCode: string,
  facebookAppId: number.isRequired,
  planTypes: arrayOf(
    shape({
      allowCoupon: bool,
      numberOfMonths: string,
      planCode: string,
      priceMonth: number,
      priceTotal: number,
      showDiscount: bool
    })
  ).isRequired,
  shopifyProducts: arrayOf(shape({ images: arrayOf(shape({ src: string, alt: string })), title: string, shopItemUrl: string, price: string }))
};

GiftFlowRouting.defaultProps = {
  currencyCode: 'USD',
  shopifyProducts: []
};

export default GiftFlowRouting;
